import React from "react";
import { Box, ThemeProvider } from "@mui/system";
import {Tooltip, IconButton, Chip} from "@mui/material";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

// Local imports
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../GenericRnDListing/GenericRnDListing";
import {TagsContainer, TagItem} from "../../Admin/ListingItems/ListingItems";
import { setData } from "@Redux/requestDataSlice";
import { rdRequestResponseFromResponses } from "../../../AppRoutes/DefaultRoutes";
import {
  stringCustomOperators,
  numberCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import {getStatusColor} from "../../../Utils/Validations/HelperFunctions/StyleFunction";

// ===================== CONSTANTS - URLs & STRINGS =====================
// API endpoints
const RD_RESPONDED_REQUESTS = "/exit-request-response/my-submissions";

// UI text constants
const PAGE_TITLE = "My Impact Assessments";
const ERROR_MESSAGE = "Failed to fetch responded exit requests. Trying again!";
const RESPONSE_STATUS_WAITING = "WAITING_FOR_PLAN";

// Tooltip text
const TOOLTIP_VIEW_DETAILS = "View Impact Assessment Details";

// Column headers
const COLUMN_HEADERS = {
  INGREDIENT: "Ingredient",
  DEADLINE: "Primary Date",
  POM_DATE: "Secondary Date",
  CATEGORY: "Category",
  REQUESTED_BY: "Requested By",
  RESPONSE_STATUS: "Impact Assessment Status",
  COMPLIANT: "Compliant",
  RESPONDED: "Responded",
  ACTIONS: "Actions",
};

// Date format
const DATE_FORMAT = "YYYY-MM-DD";

// Default values
const DEFAULT_VALUE = "-";

// Icon sizes
const ICON_SIZE = "small";
const PAGE_ICON_SIZE = "large";

// ===================== HELPER FUNCTIONS =====================
/**
 * Formats a date string using dayjs
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or default value
 */
const formatDate = (dateString) => {
  return dateString ? dayjs(dateString).format(DATE_FORMAT) : DEFAULT_VALUE;
};

/**
 * Safely accesses nested object properties
 * @param {object} obj - Object to access
 * @param {string} path - Property path (e.g., 'exitRequest.ingredient.name')
 * @returns {any} Property value or default value
 */
const getNestedValue = (obj, path) => {
  const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
  return value || DEFAULT_VALUE;
};

// ===================== MAIN COMPONENT =====================
function MyResponsesListing() {
  const dispatch = useDispatch();
  const currentUserRole = useSelector((state) => state.user.role);

  // ===== Column Configuration =====
  /**
   * DataGrid column definitions matching backend structure
   */
  const defaultColumns = [
    {
      field: "ingredient",
      headerName: COLUMN_HEADERS.INGREDIENT,
      flex: 1,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.exitRequest.ingredient.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.exitRequest.ingredient.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.2,
      type: "string",
      valueGetter: (params) => params.row.exitRequest.ingredient.name,
      filterOperators: stringCustomOperators,
      sortable: false,

    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1.2,
      type: "string",
      valueGetter: (params) => params.row.exitRequest.ingredient.casNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      valueGetter: (params) =>
        getNestedValue(params.row, "exitRequest.categoryName"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    // {
    //   field: "countries",
    //   headerName: "Region",
    //   flex: 2,
    //   minWidth: 200,
    //   filterable: false,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const rawValue = params.row.exitRequest?.countries;
    //     if (!rawValue || rawValue.length === 0) return "N/A";
    //     const grouped = rawValue.reduce((acc, item) => {
    //       const region = item?.regionName || "Unknown";
    //       const country = item?.name;
    //       if (!country) return acc;
    //       if (!acc[region]) acc[region] = [];
    //       acc[region].push(country);
    //       return acc;
    //     }, {});
    //     const items = Object.entries(grouped).map(
    //       ([region, countries]) => `${region} : ${countries.join(" - ")}`
    //     );
    //     return (
    //       <TagsContainer>
    //         {items.map((item, index) => (
    //           <TagItem key={index}>{item}</TagItem>
    //         ))}
    //       </TagsContainer>
    //     );
    //   },
    // },
    {
      field: "primaryDate",
      headerName: COLUMN_HEADERS.DEADLINE,
      flex: 1.2,
      valueGetter: (params) => {
        const dateStr = params.row.exitRequest?.primaryDate;
        return dateStr ? new Date(dateStr) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
      sortable: false,
    },
    {
      field: "secondaryDate",
      headerName: COLUMN_HEADERS.POM_DATE,
      flex: 1.2,
      valueGetter: (params) => {
        const dateStr = params.row.exitRequest?.secondaryDate;
        return dateStr ? new Date(dateStr) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
      sortable: false,
    },
    {
      field: "userEmail",
      headerName: COLUMN_HEADERS.REQUESTED_BY,
      flex: 1.1,
      valueGetter: (params) =>
        getNestedValue(params.row, "exitRequest.user.email"),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "status",
      headerName: COLUMN_HEADERS.RESPONSE_STATUS,
      flex: 1.6,
      renderCell: (params) => (
          <Chip
              label={params.row.status?.replace(/_/g, " ")}
              color={getStatusColor(params.row.status)}
              size="small"
          />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "isCompliant",
      headerName: COLUMN_HEADERS.COMPLIANT,
      flex: 1,
      type: "boolean",
      renderCell: (params) => <ComplianceIndicator params={params} />,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: COLUMN_HEADERS.RESPONDED,
      flex: 0.8,
      valueGetter: (params) => {
        const dateStr = params.row.createdAt;
        return dateStr ? new Date(dateStr) : null;
      },
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format(DATE_FORMAT) : DEFAULT_VALUE,
      type: "date",
    },
    {
      field: "actions",
      headerName: COLUMN_HEADERS.ACTIONS,
      sortable: false,
      filterable: false,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
    },
  ];
  const initialColumnVisibilityModel = {
    ingredientCasNumber: false,
    ingredientName: false,
    userEmail: false,
    secondaryDate: false,
    ulInternalDate: false,
  };

  // ===== Event Handlers =====
  /**
   * Handles navigation to response details page
   */
  const handleViewDetails = (row, navigate) => {
    dispatch(setData(row));
    const responseRoute = currentUserRole === "RD_MANAGER"
      ? DefaultRoutes.rdManagerRequestResponseFromResponses
      : DefaultRoutes.rdRequestResponseFromResponses;
    navigate(
      responseRoute
        .replace(":reqId", row.exitRequest.id)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  // ===== Render Methods =====
  /**
   * Renders the compliance indicator cell
   */
  const ComplianceIndicator = ({ params }) => {
    const isCompliant = params.row.isCompliant === true;

    return (
      <ThemeProvider theme={params.api.state.theme}>
        <Box sx={styles.complianceCellContainer}>
          <Box
            sx={{
              ...styles.complianceIndicator,
              backgroundColor: isCompliant ? "success.main" : "error.main",
            }}
          >
            {isCompliant ? (
              <CheckCircleIcon fontSize={ICON_SIZE} color="white" />
            ) : (
              <DoDisturbIcon fontSize={ICON_SIZE} color="white" />
            )}
          </Box>
        </Box>
      </ThemeProvider>
    );
  };

  /**
   * Renders action buttons for each row
   */
  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      <Tooltip title={TOOLTIP_VIEW_DETAILS}>
        <IconButton
          color="info"
          onClick={() => handleViewDetails(params.row, navigate)}
          size={ICON_SIZE}
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  // ===== Main Render =====
  return (
    <GenericRnDListing
      title={PAGE_TITLE}
      PageIcon={<MarkEmailReadIcon color="white" fontSize={PAGE_ICON_SIZE} />}
      iconColor="primary.main"
      apiRoute={RD_RESPONDED_REQUESTS}
      defaultColumns={defaultColumns}
      renderActions={renderActions}
      errorMessage={ERROR_MESSAGE}
      buttons={[]}
      serverPagination={true}
      getRowId={(row) => `${row.exitRequest.id}_${row.categoryAssignmentId}`}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
    />
  );
}

// ===================== STYLES =====================
const styles = {
  complianceCellContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  complianceIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "50%",
    color: "white",
    transform: "translateX(-16px)",
  },
};

export default MyResponsesListing;
